import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from 'react-feather';
import { Badge, Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { addResourceAPI, getResourceRulesAPI, updateResourceRuleAPI } from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import EmptyState from '../../../Components/shared/EmptyState';
import DataTableWrapper from '../../../Components/shared/DataTableWrapper';
import DrawerPanel from '../../../Components/shared/DrawerPanel';
import { companyUrl } from '../../../utils/mhNav';
import styles from './ResourceRules.module.scss';

export default function ResourceRules() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [drawer, setDrawer] = useState(null);
  const [draft, setDraft] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getResourceRulesAPI();
      setRows(res.data || []);
      setHasLoaded(true);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openDrawer = (r) => {
    setDrawer(r);
    setDraft({ ...r });
  };

  const saveDrawer = async () => {
    try {
      await updateResourceRuleAPI(drawer.id, draft);
      toast.success('Saved');
      setDrawer(null);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const columns = useMemo(
    () => [
      { name: 'Resource', selector: (r) => r.name, sortable: true },
      { name: 'Type', selector: (r) => r.resourceType, width: '110px' },
      { name: 'Capacity', selector: (r) => r.capacity, width: '90px' },
      { name: 'Max booking (min)', selector: (r) => r.maxBookingMinutes, width: '140px' },
      {
        name: 'Approval',
        cell: (r) => (r.approvalRequired ? 'Yes' : 'No'),
        width: '90px',
      },
      {
        name: 'Status',
        cell: (r) => <Badge bg={r.status === 'unavailable' ? 'danger' : 'success'}>{r.status}</Badge>,
        width: '110px',
      },
      {
        name: 'Actions',
        cell: (r) => (
          <Button size="sm" variant="outline-primary" onClick={() => openDrawer(r)}>
            Open
          </Button>
        ),
      },
    ],
    []
  );

  if (loading && !hasLoaded) {
    return (
      <div>
        <PageHeader title="Resources" breadcrumbs={[{ label: 'Company', path: companyUrl('dashboard') }, { label: 'Scheduling', path: companyUrl('scheduling') }, { label: 'Resources' }]} />
        <LoadingSkeleton rows={5} cols={4} />
      </div>
    );
  }

  if (error && !hasLoaded) {
    return (
      <div>
        <PageHeader title="Resources" breadcrumbs={[{ label: 'Company', path: companyUrl('dashboard') }, { label: 'Scheduling', path: companyUrl('scheduling') }]} />
        <EmptyState icon={AlertTriangle} title="Error" description={error.message} actionLabel="Retry" onAction={load} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Resource rules"
        breadcrumbs={[
          { label: 'Company', path: companyUrl('dashboard') },
          { label: 'Scheduling', path: companyUrl('scheduling') },
          { label: 'Resources' },
        ]}
        primaryAction={{
          label: 'Add resource',
          onClick: async () => {
            try {
              await addResourceAPI({ name: `Resource ${Date.now()}` });
              toast.success('Added');
              load();
            } catch (e) {
              toast.error(e.message);
            }
          },
        }}
      />

      {!loading && !rows.length ? (
        <EmptyState icon={AlertTriangle} title="No resources" description="Add a resource to begin." actionLabel="Add" onAction={load} />
      ) : (
        <DataTableWrapper columns={columns} data={rows} loading={loading} showSearch keyField="id" onRowClick={openDrawer} />
      )}

      <DrawerPanel isOpen={Boolean(drawer)} onClose={() => setDrawer(null)} title={draft?.name || 'Resource'} width={440}>
        {draft && (
          <>
            <div className={styles.drawerSection}>
              <Form.Label>Name</Form.Label>
              <Form.Control value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </div>
            <div className={styles.drawerSection}>
              <Form.Label>Type</Form.Label>
              <Form.Select value={draft.resourceType} onChange={(e) => setDraft({ ...draft, resourceType: e.target.value })}>
                <option>Room</option>
                <option>Equipment</option>
                <option>Virtual</option>
              </Form.Select>
            </div>
            <div className={styles.drawerSection}>
              <Form.Label>Capacity</Form.Label>
              <Form.Control type="number" value={draft.capacity} onChange={(e) => setDraft({ ...draft, capacity: +e.target.value })} />
            </div>
            <div className={styles.drawerSection}>
              <Form.Label>Max booking (minutes)</Form.Label>
              <Form.Control type="number" value={draft.maxBookingMinutes} onChange={(e) => setDraft({ ...draft, maxBookingMinutes: +e.target.value })} />
            </div>
            <div className={styles.drawerSection}>
              <Form.Check
                type="switch"
                label="Approval required"
                checked={!!draft.approvalRequired}
                onChange={(e) => setDraft({ ...draft, approvalRequired: e.target.checked })}
              />
              {draft.approvalRequired && (
                <Form.Select
                  className="mt-2"
                  value={draft.approverRole || 'tenant_admin'}
                  onChange={(e) => setDraft({ ...draft, approverRole: e.target.value })}
                >
                  <option value="tenant_admin">Tenant Admin</option>
                  <option value="scheduler">Scheduler</option>
                </Form.Select>
              )}
            </div>
            <div className={styles.drawerSection}>
              <Form.Label>Availability source</Form.Label>
              <Form.Select value={draft.availabilitySource} onChange={(e) => setDraft({ ...draft, availabilitySource: e.target.value })}>
                <option>Shared Calendar</option>
                <option>Manual</option>
                <option>External</option>
              </Form.Select>
            </div>
            <div className={styles.drawerSection}>
              <Form.Label>Blackout dates</Form.Label>
              <div className="small text-muted">{(draft.blackouts || []).join(', ') || 'None'}</div>
            </div>
            <Button onClick={saveDrawer}>Save</Button>
          </>
        )}
      </DrawerPanel>
    </div>
  );
}
